import type {
  OutcomesSummary,
  PatientOutcomesResponse,
  AttentionResponse,
  ExerciseDetail,
  ExerciseSummary,
  InAppNotification,
  MeResponse,
  OrganizationResponse,
  Page,
  PatientActivity,
  PatientDetail,
  PatientListItem,
  PendingStaffInvitation,
  PlanDetail,
  PreferencesResponse,
  ProgressResponse,
  StaffMember,
  ThreadListItem,
  ThreadResponse,
  TodayResponse,
  WeekResponse,
  ClinicalNote,
  PatientPlanVersion,
} from '@nolens/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from './api';

export const keys = {
  me: ['me'] as const,
  org: (id: string) => ['org', id] as const,
  staff: (id: string) => ['org', id, 'staff'] as const,
  staffInvites: (id: string) => ['org', id, 'staff-invites'] as const,
  patients: (id: string, q: object) => ['org', id, 'patients', q] as const,
  patient: (id: string, p: string) => ['org', id, 'patient', p] as const,
  activity: (id: string, p: string) => ['org', id, 'patient', p, 'activity'] as const,
  notes: (id: string, e: string) => ['org', id, 'episode', e, 'notes'] as const,
  plan: (id: string, p: string) => ['org', id, 'plan', p] as const,
  exercises: (id: string, q: object) => ['org', id, 'exercises', q] as const,
  exercise: (id: string, e: string) => ['org', id, 'exercise', e] as const,
  attention: (id: string, scope: string) => ['org', id, 'attention', scope] as const,
  threads: (id: string) => ['org', id, 'threads'] as const,
  thread: (id: string, p: string) => ['org', id, 'thread', p] as const,
  today: (id: string) => ['org', id, 'me', 'today'] as const,
  week: (id: string, start: string) => ['org', id, 'me', 'week', start] as const,
  progress: (id: string) => ['org', id, 'me', 'progress'] as const,
  myPlan: (id: string) => ['org', id, 'me', 'plan'] as const,
  myThread: (id: string) => ['org', id, 'me', 'messages'] as const,
  prefs: (id: string) => ['org', id, 'me', 'prefs'] as const,
  outcomes: (id: string, p: string) => ['org', id, 'patient', p, 'outcomes'] as const,
  outcomesSummary: (id: string, days: number) => ['org', id, 'outcomes-summary', days] as const,
  notifications: ['notifications'] as const,
};

const qs = (o: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const useMe = (enabled = true) => useQuery({ queryKey: keys.me, queryFn: () => api<MeResponse>('/v1/me'), enabled });
export const useOrg = (orgId: string) => useQuery({ queryKey: keys.org(orgId), queryFn: () => api<OrganizationResponse>(`/v1/orgs/${orgId}`) });
export const useStaff = (orgId: string) =>
  useQuery({ queryKey: keys.staff(orgId), queryFn: () => api<{ items: StaffMember[] }>(`/v1/orgs/${orgId}/staff`).then((r) => r.items) });
export const useStaffInvites = (orgId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.staffInvites(orgId), enabled, queryFn: () => api<{ items: PendingStaffInvitation[] }>(`/v1/orgs/${orgId}/staff-invitations`).then((r) => r.items) });

// ---- Clínica ---------------------------------------------------------------
export interface PatientFilters {
  q?: string;
  status?: string;
  mine?: boolean;
}
export const usePatients = (orgId: string, f: PatientFilters) =>
  useQuery({
    queryKey: keys.patients(orgId, f),
    queryFn: () => api<{ items: PatientListItem[] }>(`/v1/orgs/${orgId}/patients${qs({ q: f.q, status: f.status, mine: f.mine || undefined })}`).then((r) => r.items),
    placeholderData: (prev) => prev,
  });
export const usePatient = (orgId: string, patientId: string) =>
  useQuery({ queryKey: keys.patient(orgId, patientId), queryFn: () => api<PatientDetail>(`/v1/orgs/${orgId}/patients/${patientId}`) });
export const useOutcomes = (orgId: string, patientId: string) =>
  useQuery({ queryKey: keys.outcomes(orgId, patientId), queryFn: () => api<PatientOutcomesResponse>(`/v1/orgs/${orgId}/patients/${patientId}/outcomes`) });
export const useOutcomesSummary = (orgId: string, days: number) =>
  useQuery({ queryKey: keys.outcomesSummary(orgId, days), queryFn: () => api<OutcomesSummary>(`/v1/orgs/${orgId}/outcomes/summary?days=${days}`) });
export const useActivity = (orgId: string, patientId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.activity(orgId, patientId), enabled, queryFn: () => api<PatientActivity>(`/v1/orgs/${orgId}/patients/${patientId}/activity`) });
export const useNotes = (orgId: string, episodeId: string | undefined) =>
  useQuery({
    queryKey: keys.notes(orgId, episodeId ?? ''),
    enabled: !!episodeId,
    queryFn: () => api<{ items: ClinicalNote[] }>(`/v1/orgs/${orgId}/episodes/${episodeId}/notes`).then((r) => r.items),
  });
export const usePlan = (orgId: string, planId: string) =>
  useQuery({ queryKey: keys.plan(orgId, planId), queryFn: () => api<PlanDetail>(`/v1/orgs/${orgId}/plans/${planId}`) });

export interface ExerciseFilters {
  q?: string;
  region?: string;
  category?: string;
  scope?: string;
}
export const useExercises = (orgId: string, f: ExerciseFilters) =>
  useInfiniteQuery({
    queryKey: keys.exercises(orgId, f),
    initialPageParam: '',
    queryFn: ({ pageParam }) => api<Page<ExerciseSummary>>(`/v1/orgs/${orgId}/exercises${qs({ ...f, cursor: pageParam || undefined, limit: 40 })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: (prev) => prev,
  });
export const useExercise = (orgId: string, exerciseId: string | null) =>
  useQuery({ queryKey: keys.exercise(orgId, exerciseId ?? ''), enabled: !!exerciseId, queryFn: () => api<ExerciseDetail>(`/v1/orgs/${orgId}/exercises/${exerciseId}`) });

export const useAttention = (orgId: string, scope: 'mine' | 'all') =>
  useQuery({ queryKey: keys.attention(orgId, scope), queryFn: () => api<AttentionResponse>(`/v1/orgs/${orgId}/attention?scope=${scope}`), refetchInterval: 60_000 });
export const useThreads = (orgId: string) =>
  useQuery({ queryKey: keys.threads(orgId), queryFn: () => api<{ items: ThreadListItem[] }>(`/v1/orgs/${orgId}/threads`).then((r) => r.items), refetchInterval: 30_000 });
export const useThread = (orgId: string, patientId: string | null) =>
  useQuery({
    queryKey: keys.thread(orgId, patientId ?? ''),
    enabled: !!patientId,
    queryFn: () => api<ThreadResponse>(`/v1/orgs/${orgId}/patients/${patientId}/messages`),
    refetchInterval: 15_000,
  });

// ---- Utente ----------------------------------------------------------------
export const useToday = (orgId: string) => useQuery({ queryKey: keys.today(orgId), queryFn: () => api<TodayResponse>(`/v1/orgs/${orgId}/me/today`) });
export const useWeek = (orgId: string, start: string) =>
  useQuery({ queryKey: keys.week(orgId, start), queryFn: () => api<WeekResponse>(`/v1/orgs/${orgId}/me/week${qs({ start })}`) });
export const useProgress = (orgId: string) => useQuery({ queryKey: keys.progress(orgId), queryFn: () => api<ProgressResponse>(`/v1/orgs/${orgId}/me/progress`) });
export const useMyPlan = (orgId: string) =>
  useQuery({
    queryKey: keys.myPlan(orgId),
    queryFn: () => api<{ plan: PatientPlanVersion | null; history: { version: number; publishedAt: string | null; startsOn: string; changeNote: string | null }[] }>(`/v1/orgs/${orgId}/me/plan`),
  });
export const useMyThread = (orgId: string) =>
  useQuery({ queryKey: keys.myThread(orgId), queryFn: () => api<ThreadResponse>(`/v1/orgs/${orgId}/me/messages`), refetchInterval: 20_000 });
export const usePrefs = (orgId: string) => useQuery({ queryKey: keys.prefs(orgId), queryFn: () => api<PreferencesResponse>(`/v1/orgs/${orgId}/me/preferences`) });

export const useNotifications = () =>
  useQuery({ queryKey: keys.notifications, queryFn: () => api<{ items: InAppNotification[]; unread: number }>('/v1/notifications'), refetchInterval: 60_000 });

/** Mutação que invalida chaves no fim (sucesso ou erro). */
export function useApiMutation<TBody, TResult = void>(
  fn: (body: TBody) => Promise<TResult>,
  invalidate: QueryKey[] = [],
  onSuccess?: (r: TResult) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => onSuccess?.(r),
    onSettled: () => Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k }))),
  });
}
